import {
  Link,
  type useLocation,
  type AnyRouteMatch,
} from "@tanstack/react-router";
import { type i18n as I18n } from "i18next";
import { type JSX, use } from "react";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "ui";

import { type MaybeAuthAuthenticate } from "src/auth/AuthStore";
import { AthleteBreadcrumbSwitcher } from "src/components/AthleteBreadcrumbSwitcher";
import { useBreadcrumbsContext } from "src/contexts/BreadcrumbsContext";
import { type TranslationKey, type BreadcrumbData } from "src/types";

export interface BreadcrumbContext {
  i18n: I18n;
  session: MaybeAuthAuthenticate;
  location: ReturnType<typeof useLocation>;
  pathname: string;
}

function* interperse<T>(arr: T[], sep: (i: number) => T) {
  yield arr[0];

  for (let i = 1; i < arr.length; i++) {
    yield sep(i);
    yield arr[i];
  }
}

type BreadcrumbLoaderValue =
  | BreadcrumbData
  | BreadcrumbData[]
  | Promise<BreadcrumbData | BreadcrumbData[]>;

function isMatchWithLoaderBreadcrumb(
  match: AnyRouteMatch,
): match is AnyRouteMatch & {
  loaderData: { breadcrumb: BreadcrumbLoaderValue };
} {
  return (
    match.loaderData &&
    typeof match.loaderData === "object" &&
    "breadcrumb" in match.loaderData &&
    match.loaderData.breadcrumb !== undefined
  );
}

function isMatchWithStaticDataBreadcrumb(
  match: AnyRouteMatch,
): match is AnyRouteMatch & {
  staticData: {
    getBreadcrumb: (
      ctx: BreadcrumbContext & { params: Record<string, string> },
    ) => BreadcrumbData;
  };
} {
  return (
    match.staticData &&
    typeof match.staticData === "object" &&
    "getBreadcrumb" in match.staticData &&
    typeof match.staticData.getBreadcrumb === "function"
  );
}

function resolveLabel(
  breadcrumb: BreadcrumbData,
  ctx: BreadcrumbContext,
): string {
  const role = ctx.session?.coach
    ? "coach"
    : ctx.session?.athlete
      ? "athlete"
      : null;

  let label: string | undefined;
  let translationKey: TranslationKey | undefined;

  if (role && breadcrumb[role]) {
    label = breadcrumb[role]?.label;
    translationKey = breadcrumb[role]?.labelTranslationKey;
  }

  if (!label && !translationKey) {
    label = breadcrumb.label;
    translationKey = breadcrumb.labelTranslationKey;
  }

  if (translationKey) {
    return ctx.i18n.t(translationKey);
  }

  return label || "";
}

interface ResolvedBreadcrumb {
  label: string;
  href: string;
  athleteSwitcher?: {
    athleteId: string;
  };
}

function getComponent(
  breadcrumb: ResolvedBreadcrumb,
  options: {
    isCurrentPage: boolean;
    pathname: string;
  },
) {
  if (breadcrumb.athleteSwitcher) {
    return (
      <AthleteBreadcrumbSwitcher
        athleteHref={breadcrumb.href}
        athleteId={breadcrumb.athleteSwitcher.athleteId}
        athleteName={breadcrumb.label}
        isCurrentPage={options.isCurrentPage}
        pathname={options.pathname}
      />
    );
  }

  if (options.isCurrentPage) {
    return <BreadcrumbPage>{breadcrumb.label}</BreadcrumbPage>;
  }

  return (
    <BreadcrumbLink asChild>
      <Link to={breadcrumb.href}>{breadcrumb.label}</Link>
    </BreadcrumbLink>
  );
}

export function createBreadcrumbs(
  matches: AnyRouteMatch[],
  ctx: BreadcrumbContext,
) {
  const breadcrumbContext = useBreadcrumbsContext();

  const breadcrumbs: ResolvedBreadcrumb[] = [];

  for (const match of matches) {
    let matchBreadcrumbs: BreadcrumbData[] = [];

    if (isMatchWithLoaderBreadcrumb(match)) {
      const breadcrumbData = match.loaderData.breadcrumb;
      const resolved =
        breadcrumbData instanceof Promise
          ? use(breadcrumbData)
          : breadcrumbData;
      matchBreadcrumbs = Array.isArray(resolved) ? resolved : [resolved];
    } else if (isMatchWithStaticDataBreadcrumb(match)) {
      matchBreadcrumbs = [
        match.staticData.getBreadcrumb({
          i18n: ctx.i18n,
          session: ctx.session,
          location: ctx.location,
          params: match.params,
          pathname: ctx.pathname,
        }),
      ];
    }

    for (const breadcrumb of matchBreadcrumbs) {
      const override =
        breadcrumb.id !== undefined
          ? breadcrumbContext.getOverrideById(breadcrumb.id)
          : undefined;

      const label = override?.value ?? resolveLabel(breadcrumb, ctx);

      if (label === "") {
        continue;
      }

      breadcrumbs.push({
        label,
        href: breadcrumb.href,
        athleteSwitcher: breadcrumb.athleteSwitcher,
      });
    }
  }

  if (breadcrumbs.length === 0) {
    return null;
  }

  const components: JSX.Element[] = breadcrumbs.map((breadcrumb, i) => {
    const component = getComponent(breadcrumb, {
      isCurrentPage: i === breadcrumbs.length - 1,
      pathname: ctx.pathname,
    });

    return <BreadcrumbItem key={i}>{component}</BreadcrumbItem>;
  });

  return (
    <Breadcrumb>
      <BreadcrumbList>
        {Array.from(
          interperse(components, (index) => {
            return <BreadcrumbSeparator key={`${index}-separator`} />;
          }),
        )}
      </BreadcrumbList>
    </Breadcrumb>
  );
}
